import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, Link2, Unlink, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

/**
 * Gmail connection panel — lives on My Profile. Handles connect / reconnect /
 * disconnect for the currently-signed-in HISPL user's Gmail account.
 */
export default function GmailSettingsCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      const [{ data: cfg }, { data: st }] = await Promise.all([
        api.get("/gmail/config-status"),
        api.get("/gmail/status"),
      ]);
      setStatus({ ...cfg, ...st });
    } catch {
      setStatus({ configured: false, connected: false, message: "Failed to load status" });
    }
  }

  useEffect(() => {
    refresh();
    const params = new URLSearchParams(window.location.search);
    const state = params.get("gmail");
    if (state === "connected") {
      toast.success("Gmail connected");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (state === "error") {
      const reasonMap = {
        not_configured: "Gmail OAuth is not configured on the server. Ask your Administrator.",
        missing_params: "Google did not return the expected authorization response.",
        invalid_state: "This connection link has expired. Please click Connect Gmail again.",
        token_exchange_failed: "Google rejected the sign-in. Please try Connect Gmail again.",
        persistence_failed: "We could not save your Gmail connection. Please retry.",
        access_denied: "You cancelled the Google consent screen.",
        consent_denied: "You cancelled the Google consent screen.",
      };
      const reason = params.get("reason") || "";
      toast.error(reasonMap[reason] || `Gmail connection failed (${reason || "unknown"}).`);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  async function connect() {
    setBusy(true);
    try {
      const { data } = await api.get("/gmail/connect");
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Cannot start OAuth");
      setBusy(false);
    }
  }

  async function disconnect() {
    setBusy(true);
    try {
      await api.post("/gmail/disconnect");
      toast.success("Gmail disconnected");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to disconnect");
    } finally { setBusy(false); }
  }

  if (!status) return <Card className="p-5"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></Card>;

  return (
    <Card className="p-5" data-testid="gmail-settings-card">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
        <Mail className="w-3.5 h-3.5" /> Email Integration
      </div>
      <div className="text-lg font-semibold text-slate-900 mt-1">Gmail</div>
      <p className="text-xs text-slate-500 mt-1">
        Connect your Google account so HISPL NEXUS can send quotations, order acceptances and
        other documents on your behalf. HISPL never stores your Gmail password — only a Google
        OAuth refresh token is kept.
      </p>

      {!status.configured && (
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">Gmail OAuth is not configured on the server yet.</div>
            <div className="mt-1">Ask your Administrator to add <code className="font-mono">GOOGLE_CLIENT_ID</code>, <code className="font-mono">GOOGLE_CLIENT_SECRET</code> and <code className="font-mono">GMAIL_REDIRECT_URI</code> to backend/.env and to whitelist that redirect URI in Google Cloud Console.</div>
          </div>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm">
          {status.connected ? (
            <div className="flex items-center gap-2 text-emerald-700">
              <CheckCircle2 className="w-4 h-4" />
              <span>Connected as <span className="font-mono font-semibold" data-testid="gmail-connected-email">{status.email || "(unknown)"}</span></span>
            </div>
          ) : (
            <span className="text-slate-500">Not connected</span>
          )}
        </div>
        <div className="flex gap-2">
          {status.connected ? (
            <>
              <Button variant="outline" size="sm" onClick={connect} disabled={busy || !status.configured} data-testid="gmail-reconnect-btn">
                <Link2 className="w-3.5 h-3.5 mr-1" /> Reconnect
              </Button>
              <Button variant="outline" size="sm" onClick={disconnect} disabled={busy} data-testid="gmail-disconnect-btn">
                <Unlink className="w-3.5 h-3.5 mr-1" /> Disconnect
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={connect} disabled={busy || !status.configured} data-testid="gmail-connect-btn"
                    className="bg-red-700 hover:bg-red-800 text-white">
              <Link2 className="w-3.5 h-3.5 mr-1" /> Connect Gmail
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
